import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Header.css';
const Header = () => {
  const MOBILE_BREAKPOINT = 1024;
  const MINIMUM_RIGHT_GAP = 96;
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isCompactNav, setIsCompactNav] = useState(window.innerWidth <= MOBILE_BREAKPOINT);
  const headerRef = useRef(null);
  const headerContentRef = useRef(null);
  const logoRef = useRef(null);
  const measureNavRef = useRef(null);
  const navRef = useRef(null);
  const navIndicatorRef = useRef(null);

  const moveNavIndicator = (target) => {
    const indicator = navIndicatorRef.current;
    if (!indicator || isCompactNav || !target) return;
    indicator.style.left = `${target.offsetLeft}px`;
    indicator.style.width = `${target.offsetWidth}px`;
    indicator.style.opacity = '1';
  };

  const moveIndicatorToActiveLink = () => {
    const activeLink = navRef.current?.querySelector('.nav-link.active');
    if (activeLink) {
      moveNavIndicator(activeLink);
    } else if (navIndicatorRef.current) {
      navIndicatorRef.current.style.opacity = '0';
    }
  };

  const primaryLinks = [
    { to: '/', label: 'Home', active: location.pathname === '/' },
    { to: '/browse', label: 'Browse Events', active: location.pathname === '/browse' },
    { to: '/about', label: 'About', active: location.pathname === '/about' },
    { to: '/support', label: 'Support', active: location.pathname === '/support' }
  ];

  const accountLinks = [];

  if (user?.account_type === 'user') {
    accountLinks.push(
      { to: '/my-bookings', label: 'My Bookings', active: location.pathname === '/my-bookings' },
      { to: '/my-spending', label: 'My Spending', active: location.pathname === '/my-spending' },
      { to: '/profile', label: 'Profile', active: location.pathname === '/profile' }
    );
  }

  if (user?.account_type === 'creator') {
    accountLinks.push(
      { to: '/create-event', label: 'Create Event', active: location.pathname === '/create-event' },
      { to: '/manage-events', label: 'Manage Events', active: location.pathname === '/manage-events' },
      { to: '/creator-dashboard', label: 'Dashboard', active: location.pathname === '/creator-dashboard' },
      { to: '/revenue', label: 'Revenue', active: location.pathname === '/revenue' },
      { to: '/ticket-validation', label: 'Validate Tickets', active: location.pathname === '/ticket-validation' },
      { to: '/profile', label: 'Profile', active: location.pathname === '/profile' }
    );
  }

  if (user?.is_admin) {
    accountLinks.push(
      { to: '/admin', label: 'Admin Panel', active: location.pathname === '/admin' },
      { to: '/profile', label: 'Profile', active: location.pathname === '/profile' }
    );
  }

  const navLinks = [...primaryLinks, ...accountLinks];
  const isJoinPage = location.pathname === '/join' || location.pathname === '/register-creator';
  const isLoginPage = location.pathname === '/auth' && !isJoinPage;

  const recalculateNavMode = () => {
    const shouldUseCompactNav = window.innerWidth <= MOBILE_BREAKPOINT;

    if (shouldUseCompactNav) {
      setMobileMenuOpen(false);
      setIsCompactNav(true);
      return;
    }

    const headerContent = headerContentRef.current;
    const logo = logoRef.current;
    const measureNav = measureNavRef.current;

    if (!headerContent || !logo || !measureNav) {
      setIsCompactNav(false);
      return;
    }

    const availableWidth = headerContent.clientWidth;
    const requiredWidth = logo.offsetWidth + measureNav.scrollWidth + MINIMUM_RIGHT_GAP;
    const needsCompactNav = requiredWidth > availableWidth;

    if (needsCompactNav) {
      setMobileMenuOpen(false);
    }

    setIsCompactNav(needsCompactNav);
  };

  useLayoutEffect(() => {
    recalculateNavMode();
    const frame = requestAnimationFrame(moveIndicatorToActiveLink);
    return () => cancelAnimationFrame(frame);
  }, [location.pathname, user, isCompactNav]);

  useEffect(() => {
    const handleResize = () => {
      recalculateNavMode();
      requestAnimationFrame(moveIndicatorToActiveLink);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [user]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (user && (user.account_type === 'creator' || user.account_type === 'admin' || user.is_admin)) {
    return null;
  }

  const renderNavContent = () => (
    <>
      {navLinks.map((link) => (
        <Link
          key={link.to}
          to={link.to}
          className={`nav-link ${link.active ? 'active' : ''}`}
          onMouseEnter={(event) => moveNavIndicator(event.currentTarget)}
          onFocus={(event) => moveNavIndicator(event.currentTarget)}
        >
          {link.label}
        </Link>
      ))}

      {user ? (
        <>
          <button
            type="button"
            onClick={() => {
              logout();
              navigate('/');
            }}
            className="nav-button logout"
          >
            Logout
          </button>
        </>
      ) : (
        <>
          <Link to="/auth" className={`nav-button login${isLoginPage ? ' active' : ''}`}>
            Sign In
          </Link>
          <Link to="/join" className={`nav-button primary${isJoinPage ? ' active' : ''}`}>
            Join
          </Link>
        </>
      )}
    </>
  );

  return (
    <header className="luxury-header" ref={headerRef}>
      <div className={`header-content ${isCompactNav ? 'compact' : ''}`} ref={headerContentRef}>
        <Link to="/" className="header-logo" ref={logoRef}>
          <img src="/assets/images/image.png" alt="Tickets Lord Logo" className="logo-image" />
        </Link>

        <div className="header-nav-measure" ref={measureNavRef} aria-hidden="true">
          <nav className="header-nav header-nav-clone">
            {navLinks.map((link) => (
              <span key={link.to} className={`nav-link ${link.active ? 'active' : ''}`}>
                {link.label}
              </span>
            ))}
            {user ? (
              <>
                <span className="nav-button logout">Logout</span>
              </>
            ) : (
              <>
                <span className="nav-button login">Sign In</span>
                <span className="nav-button primary">Join</span>
              </>
            )}
          </nav>
        </div>

        {isCompactNav && (
          <button
            type="button"
            className={`mobile-menu-toggle ${mobileMenuOpen ? 'active' : ''}`}
            onClick={() => setMobileMenuOpen((open) => !open)}
            aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={mobileMenuOpen}
            aria-controls="header-navigation"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        )}

        <nav
          id="header-navigation"
          ref={navRef}
          className={`header-nav ${mobileMenuOpen ? 'open' : ''}`}
          onMouseLeave={moveIndicatorToActiveLink}
        >
          <span className="nav-indicator" ref={navIndicatorRef} aria-hidden="true" />
          {renderNavContent()}
        </nav>
      </div>

      <div className="header-decoration">
        <div className="decoration-line"></div>
        <div className="decoration-dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    </header>
  );
};

export default Header;
